<template>
  <div class="auth-manage">
    <!-- 子标签切换 -->
    <div class="auth-tabs">
      <div class="auth-tab" :class="{ active: subTab === 'devices' }" @click="subTab = 'devices'">
        设备管理
      </div>
      <div class="auth-tab" :class="{ active: subTab === 'codes' }" @click="subTab = 'codes'">
        授权码管理
      </div>
    </div>

    <!-- ========== 授权码管理 ========== -->
    <div v-if="subTab === 'codes'" class="auth-section">
      <!-- 创建授权码 -->
      <div class="section-card">
        <div class="section-header">
          <span class="section-title">创建授权码</span>
        </div>
        <div class="create-row">
          <NInput
            v-model:value="newCode"
            placeholder="输入授权码（支持任意字符）"
            size="large"
            @keyup.enter="handleCreateCode"
          />
          <NButton type="primary" size="large" @click="handleCreateCode" :loading="creating">
            创建
          </NButton>
        </div>
      </div>

      <!-- 未使用授权码列表 -->
      <div class="section-card">
        <div class="section-header">
          <span class="section-title">未使用授权码</span>
          <span class="section-count">共 {{ codeTotal }} 个</span>
        </div>
        <div class="code-list">
          <div class="code-item" v-for="item in codeList" :key="item.id">
            <div class="code-info">
              <span class="code-value">{{ item.code }}</span>
              <span class="code-time">{{ formatTime(item.create_time) }}</span>
            </div>
            <NPopconfirm @positive-click="handleDeleteCode(item.id!)">
              <template #trigger>
                <NButton size="small" type="error" quaternary>删除</NButton>
              </template>
              确定删除授权码 "{{ item.code }}" 吗？
            </NPopconfirm>
          </div>
          <div v-if="codeList.length === 0" class="empty-tip">暂无未使用的授权码</div>
        </div>
        <div class="pagination-wrap" v-if="codeTotalPages > 1">
          <NPagination
            v-model:page="codePage"
            :page-count="codeTotalPages"
            :page-slot="5"
            size="small"
            @update:page="loadCodeList"
          />
        </div>
      </div>
    </div>

    <!-- ========== 设备管理 ========== -->
    <div v-if="subTab === 'devices'" class="auth-section">
      <!-- 设备列表 -->
      <div class="section-card">
        <div class="section-header">
          <span class="section-title">已绑定设备</span>
          <span class="section-count">共 {{ deviceTotal }} 台</span>
        </div>
        <!-- 筛选栏 -->
        <div class="filter-row">
          <div class="filter-item">
            <span class="filter-label">状态</span>
            <NSelect
              v-model:value="deviceStatusFilter"
              :options="statusOptions"
              size="small"
              style="width: 100px"
              @update:value="loadDeviceList(1)"
            />
          </div>
          <div class="filter-item">
            <span class="filter-label">排序</span>
            <NSelect
              v-model:value="deviceSort"
              :options="sortOptions"
              size="small"
              style="width: 140px"
              @update:value="loadDeviceList(1)"
            />
          </div>
        </div>
        <!-- 设备卡片 -->
        <div class="device-list">
          <div class="device-item" v-for="item in deviceList" :key="item.id">
            <div class="device-main">
              <div class="device-header">
                <span class="device-id" :title="item.device_id">{{ truncateId(item.device_id) }}</span>
                <NTag :type="item.status === 1 ? 'success' : 'error'" size="small">
                  {{ item.status === 1 ? '已启用' : '已禁用' }}
                </NTag>
              </div>
              <div class="device-meta">
                <div class="meta-row">
                  <span class="meta-label">授权码</span>
                  <span class="meta-value">{{ item.auth_code }}</span>
                </div>
                <div class="meta-row">
                  <span class="meta-label">绑定时间</span>
                  <span class="meta-value">{{ formatTime(item.bind_time) }}</span>
                </div>
                <div class="meta-row">
                  <span class="meta-label">最后登录</span>
                  <span class="meta-value">{{ formatTime(item.last_login_time) }}</span>
                </div>
              </div>
            </div>
            <div class="device-actions">
              <div class="device-action-item">
                <span class="device-action-label">{{ item.status === 1 ? '已启用' : '已禁用' }}</span>
                <NSwitch
                  :value="item.status === 1"
                  @update:value="(val: boolean) => handleToggleStatus(item, val)"
                />
              </div>
            </div>
            <NPopconfirm @positive-click="handleDeleteDevice(item.id!)">
              <template #trigger>
                <NButton class="device-delete-btn" size="tiny" quaternary type="error">删除</NButton>
              </template>
              确定删除该设备吗？
              <br />
              删除后该设备将无法使用插件。
            </NPopconfirm>
          </div>
          <div v-if="deviceList.length === 0" class="empty-tip">暂无绑定设备</div>
        </div>
        <div class="pagination-wrap" v-if="deviceTotalPages > 1">
          <NPagination
            v-model:page="devicePage"
            :page-count="deviceTotalPages"
            :page-slot="5"
            size="small"
            @update:page="loadDeviceList"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import {
  NInput,
  NButton,
  NSelect,
  NTag,
  NSwitch,
  NPagination,
  NPopconfirm,
  createDiscreteApi,
} from 'naive-ui';
import Tools from '@/ts/Tools';

const api = createDiscreteApi(['message']);

// ---- 子标签 ----
const subTab = ref<'codes' | 'devices'>('devices');

// ========== 授权码管理 ==========
const newCode = ref('');
const creating = ref(false);
const codeList = ref<any[]>([]);
const codePage = ref(1);
const codeTotal = ref(0);
const codeTotalPages = ref(1);
const codeLimit = 20;

async function loadCodeList(page: number = 1) {
  Tools.FetchGetAsync<{
    total: number;
    page: number;
    limit: number;
    total_pages: number;
    list: any[];
  }>('Auth/GetUnusedAuthCodeList', { page, limit: codeLimit }).then((resp) => {
    if (resp.CheckIsOk()) {
      codeList.value = resp.Data?.list ?? [];
      codeTotal.value = resp.Data?.total ?? 0;
      codeTotalPages.value = resp.Data?.total_pages ?? 1;
      codePage.value = page;
    }
  });
}

async function handleCreateCode() {
  const code = newCode.value.trim();
  if (!code) {
    Tools.AlertError('授权码不能为空');
    return;
  }
  creating.value = true;
  Tools.FetchAsync('Auth/CreateAuthCode', { code }, 'POST').then((resp) => {
    if (resp.CheckIsOk()) {
      Tools.AlertSuccess('授权码创建成功');
      newCode.value = '';
      loadCodeList(codePage.value);
    }
    creating.value = false;
  }).catch(() => { creating.value = false; });
}

async function handleDeleteCode(id: number) {
  Tools.FetchGetAsync('Auth/DeleteUnusedAuthCode', { id }).then((resp) => {
    if (resp.CheckIsOk()) {
      Tools.AlertSuccess('删除成功');
      loadCodeList(codePage.value);
    }
  });
}

// ========== 设备管理 ==========
const deviceList = ref<any[]>([]);
const devicePage = ref(1);
const deviceTotal = ref(0);
const deviceTotalPages = ref(1);
const deviceLimit = 20;
const deviceStatusFilter = ref(-1);
const deviceSort = ref('bind_time');

const statusOptions = [
  { label: '全部', value: -1 },
  { label: '已启用', value: 1 },
  { label: '已禁用', value: 0 },
];

const sortOptions = [
  { label: '绑定时间 ↓', value: 'bind_time' },
  { label: '最后登录 ↓', value: 'last_login' },
  { label: '状态优先', value: 'status' },
];

async function loadDeviceList(page: number = 1) {
  Tools.FetchGetAsync<{
    total: number;
    page: number;
    limit: number;
    total_pages: number;
    list: any[];
  }>('Auth/GetDeviceList', {
    page,
    limit: deviceLimit,
    status: deviceStatusFilter.value,
    sort: deviceSort.value,
  }).then((resp) => {
    if (resp.CheckIsOk()) {
      deviceList.value = resp.Data?.list ?? [];
      deviceTotal.value = resp.Data?.total ?? 0;
      deviceTotalPages.value = resp.Data?.total_pages ?? 1;
      devicePage.value = page;
    }
  });
}

async function handleToggleStatus(item: any, enabled: boolean) {
  const newStatus = enabled ? 1 : 0;
  Tools.FetchAsync('Auth/SetDeviceStatus', { id: item.id, status: newStatus }, 'POST').then((resp) => {
    if (resp.CheckIsOk()) {
      item.status = newStatus;
      Tools.AlertSuccess(enabled ? '设备已启用' : '设备已禁用');
    }
  });
}

async function handleDeleteDevice(id: number) {
  Tools.FetchGetAsync('Auth/DeleteDevice', { id }).then((resp) => {
    if (resp.CheckIsOk()) {
      Tools.AlertSuccess('设备已删除');
      loadDeviceList(devicePage.value);
    }
  });
}

// ---- 工具函数 ----
function formatTime(unix: number | null | undefined): string {
  if (!unix) return '-';
  const date = new Date(unix * 1000);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');
  return `${y}-${m}-${d} ${h}:${min}:${s}`;
}

function truncateId(id: string): string {
  if (!id) return '-';
  if (id.length <= 12) return id;
  return id.substring(0, 8) + '...' + id.substring(id.length - 4);
}

onMounted(() => {
  loadCodeList();
  loadDeviceList();
});
</script>

<style scoped lang="scss">
.auth-manage {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

// 子标签
.auth-tabs {
  display: flex;
  gap: 4px;
  background-color: rgb(40, 40, 40);
  border-radius: 10px;
  padding: 4px;

  .auth-tab {
    flex: 1;
    text-align: center;
    padding: 10px 0;
    font-size: 13px;
    font-weight: 500;
    color: #888;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;

    &.active {
      background-color: rgb(51, 51, 51);
      color: #F59E0B;
    }

    &:hover:not(.active) {
      color: #ccc;
    }
  }
}

.auth-section {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

// ---- 公用卡片 ----
.section-card {
  background-color: rgb(51, 51, 51);
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  .section-title {
    font-size: 15px;
    font-weight: 600;
    color: #fff;
  }

  .section-count {
    font-size: 12px;
    color: #888;
  }
}

// 创建行
.create-row {
  display: flex;
  gap: 10px;

  :deep(.n-input) {
    flex: 1;
  }

  :deep(.n-button) {
    flex-shrink: 0;
    min-width: 80px;
  }
}

// 授权码列表
.code-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 45vh;
  overflow-y: auto;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 2px;
  }

  .code-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 14px;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.02) 100%);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 10px;
    transition: all 0.2s;

    &:hover {
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.04) 100%);
      border-color: rgba(255, 255, 255, 0.15);
    }

    .code-info {
      display: flex;
      flex-direction: column;
      gap: 4px;
      min-width: 0;

      .code-value {
        font-size: 15px;
        font-weight: 600;
        color: #ffd700;
        word-break: break-all;
      }

      .code-time {
        font-size: 11px;
        color: #666;
      }
    }
  }

  .empty-tip {
    text-align: center;
    padding: 30px 20px;
    color: #666;
    font-size: 13px;
    background: rgba(255, 255, 255, 0.03);
    border-radius: 10px;
    border: 1px dashed rgba(255, 255, 255, 0.08);
  }
}

// 筛选行
.filter-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;

  .filter-item {
    display: flex;
    align-items: center;
    gap: 6px;

    .filter-label {
      font-size: 12px;
      color: #888;
    }
  }
}

// 设备列表
.device-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 50vh;
  overflow-y: auto;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 2px;
  }

  .device-item {
    position: relative;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.02) 100%);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 10px;
    transition: all 0.2s;

    &:hover {
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.04) 100%);
      border-color: rgba(255, 255, 255, 0.15);
    }

    .device-main {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 10px;

      .device-header {
        display: flex;
        align-items: center;
        gap: 8px;

        .device-id {
          font-size: 14px;
          font-weight: 600;
          color: #64d2ff;
          font-family: monospace;
        }
      }

      .device-meta {
        display: flex;
        flex-direction: column;
        gap: 4px;

        .meta-row {
          display: flex;
          gap: 8px;

          .meta-label {
            font-size: 11px;
            color: #666;
            min-width: 56px;
          }

          .meta-value {
            font-size: 12px;
            color: #ccc;
          }
        }
      }
    }

    .device-actions {
      display: flex;
      align-items: center;
      flex-shrink: 0;
      padding-left: 14px;
    }

    .device-action-item {
      display: flex;
      align-items: center;
      gap: 8px;

      .device-action-label {
        font-size: 11px;
        color: #888;
        min-width: 36px;
      }
    }

    .device-delete-btn {
      position: absolute;
      right: 12px;
      bottom: 12px;
    }
  }

  .empty-tip {
    text-align: center;
    padding: 30px 20px;
    color: #666;
    font-size: 13px;
    background: rgba(255, 255, 255, 0.03);
    border-radius: 10px;
    border: 1px dashed rgba(255, 255, 255, 0.08);
  }
}

// 分页
.pagination-wrap {
  display: flex;
  justify-content: center;
  padding-top: 4px;
}
</style>
